import {
  signIn,
  signOut,
  fetchUserAttributes,
  getCurrentUser,
  confirmResetPassword,
  resetPassword,
  confirmSignIn,
  fetchAuthSession,
  signInWithRedirect
} from 'aws-amplify/auth';
import { CognitoHostedUIIdentityProvider } from './types';
import { logger } from 'core/utils/logger';

export type UserAttributes = {
  email: string;
  sub: string;
  [key: string]: string;
};

export type AuthUser = {
  username: string;
  attributes: UserAttributes;
};

export const authService = {
  signIn: async (username: string, password: string): Promise<AuthUser> => {
    try {
      const result = await signIn({
        username: username.toLowerCase(),
        password
      });

      if (result.isSignedIn) {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        return {
          username: user.username,
          attributes: attributes as UserAttributes
        };
      } else if (
        result.nextStep?.signInStep ===
        'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        // Return a partial user object that can be used for completeNewPassword
        return {
          username,
          attributes: { email: username, sub: '' }
        };
      } else {
        throw new Error(
          `Unexpected authentication state: ${result.nextStep?.signInStep}`
        );
      }
    } catch (error) {
      logger.error(error, 'Error signing in');
      throw error;
    }
  },

  federatedSignIn: async ({
    provider,
    customState
  }: {
    provider: CognitoHostedUIIdentityProvider | string;
    customState?: string;
  }) => {
    try {
      await signInWithRedirect({
        provider:
          provider === CognitoHostedUIIdentityProvider.Google
            ? 'Google'
            : {
                custom: provider
              },
        ...(customState ? { customState } : {})
      });
    } catch (error) {
      logger.error(error, 'Error in federated sign in');
      throw error;
    }
  },

  signOut: async (): Promise<void> => {
    try {
      await signOut({ global: true });
    } catch (error) {
      logger.error(error, 'Error signing out');
      throw error;
    }
  },

  forgotPassword: async (email: string): Promise<void> => {
    try {
      await resetPassword({ username: email.toLowerCase() });
    } catch (error) {
      logger.error(error, 'Error in forgot password');
      throw error;
    }
  },

  forgotPasswordSubmit: async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> => {
    try {
      await confirmResetPassword({
        username: email.toLowerCase(),
        confirmationCode: code,
        newPassword
      });
    } catch (error) {
      logger.error(error, 'Error in reset password submit');
      throw error;
    }
  },

  completeNewPassword: async (
    _username: string, // Not used directly but kept for API compatibility
    newPassword: string
  ): Promise<AuthUser> => {
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword
      });

      if (result.isSignedIn) {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        return {
          username: user.username,
          attributes: attributes as UserAttributes
        };
      } else {
        throw new Error(
          `Unexpected authentication state: ${result.nextStep?.signInStep}`
        );
      }
    } catch (error) {
      logger.error(error, 'Error completing new password');
      throw error;
    }
  },

  currentAuthenticatedUser: async (): Promise<AuthUser> => {
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      return {
        username: user.username,
        attributes: attributes as UserAttributes
      };
    } catch (error) {
      logger.error(error, 'Error getting current authenticated user');
      throw error;
    }
  },

  currentSession: async () => {
    try {
      return await fetchAuthSession();
    } catch (error) {
      logger.error(error, 'Error getting current session');
      throw error;
    }
  }
};
